import { Button, Input } from '@root/components'
import ColorPicker from '@root/components/ColorPicker/ColorPicker'
import { TAG_COLORS } from '@root/constants'
import { useBusinessContext } from '@root/context'
import { createTag, ITag, updateTag } from '@root/query/tags'
import { onCtrlEnter, setAPIErrors } from '@root/utils'
import React from 'react'

import { useForm, get, Controller } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { FaSave } from 'react-icons/fa'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import styled from 'styled-components'

interface Props {
  handleFormClose: () => void
  onCreated?: (tagId: number) => void
  tag?: ITag
}

const TagForm: React.FC<Props> = ({ handleFormClose, tag, onCreated }) => {
  const [t] = useTranslation()
  const { slug } = useBusinessContext()
  const queryClient = useQueryClient()

  const {
    register,
    handleSubmit,
    setError,
    control,
    formState: { errors }
  } = useForm<Partial<ITag>>({
    defaultValues: {
      name: tag?.name || '',
      description: tag?.description || '',
      color: tag?.color || TAG_COLORS[0]
    }
  })

  const createTagMutation = useMutation<ITag, unknown, Partial<ITag>>({
    mutationFn: (data: Partial<ITag>) => createTag({ slug }, data),
    ...{
      onSuccess: data => {
        toast.success(t('settings.tags.form.successCreate'))
        // Invalidating the whole slug
        // query to get the has_tags value to refresh
        queryClient.invalidateQueries({ queryKey: [slug] })
        onCreated?.(data.id)
        handleFormClose()
      },
      onError: error => {
        const { status, data } = (error as { status?: number; data?: any }) ?? {}
        status === 400 && setAPIErrors(data, setError)
        toast.error(t('general.error'))
      }
    }
  })

  const updateTagMutation = useMutation<ITag, unknown, Partial<ITag>>({
    mutationFn: (data: Partial<ITag>) => updateTag({ slug, tagId: tag.id }, data),
    ...{
      onSuccess: () => {
        toast.success(t('settings.tags.form.successUpdate'))
        queryClient.invalidateQueries({ queryKey: [slug, 'tags'] })
        handleFormClose()
      },
      onError: error => {
        const { status, data } = (error as { status?: number; data?: any }) ?? {}
        status === 400 && setAPIErrors(data, setError)
        toast.error(t('general.error'))
      }
    }
  })

  const onSubmit = async (data: ITag) => {
    try {
      tag ? updateTagMutation.mutate(data) : createTagMutation.mutate(data)
    } catch (error) {
      console.error(error)
      return null
    }
  }

  const isLoading = createTagMutation.isPending

  return (
    <StyledForm id="tag-form" onKeyDown={e => onCtrlEnter(e, handleSubmit(onSubmit))}>
      <StyledFormInputs>
        <Input
          required={true}
          label={t('settings.tags.form.name')}
          {...register('name', {
            required: { value: true, message: t('validation.required') }
          })}
          errors={get(errors, 'name')}
          autoFocus
        ></Input>

        <Input
          label={t('settings.tags.form.description')}
          {...register('description')}
          errors={get(errors, 'description')}
        ></Input>

        <Controller
          control={control}
          name="color"
          render={({ field: { onChange, value } }) => (
            <ColorPicker
              availableColors={TAG_COLORS}
              setSelectedColor={(color: string) => onChange(color)}
              selectedColor={value}
            ></ColorPicker>
          )}
        ></Controller>
      </StyledFormInputs>

      <StyledFooter>
        <Button type="button" onClick={() => handleFormClose()}>
          {t('general.cancel')}
        </Button>
        <Button
          showSpinner={isLoading}
          type="button"
          intent="success"
          icon={<FaSave />}
          onClick={e => {
            e.preventDefault()
            e.stopPropagation()
            handleSubmit(onSubmit)()
          }}
        >
          {t('general.save')}
        </Button>
      </StyledFooter>
    </StyledForm>
  )
}

export default TagForm

const StyledForm = styled.form`
  display: flex;
  height: 100%;
  flex-direction: column;
  justify-content: space-between;
`

const StyledFormInputs = styled.div`
  display: flex;
  flex-direction: column;
`

const StyledFooter = styled.div`
  display: flex;
  justify-content: space-between;
`
